import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { withRouter, Link, useLocation } from 'react-router-dom';
import { stringify } from 'qs';
import numeral from 'numeral';
import AddCartIcon from 'material-design-icons/action/svg/production/ic_add_shopping_cart_24px.svg';
import RemoveCartIcon from 'material-design-icons/action/svg/production/ic_remove_shopping_cart_24px.svg';
import { pathOr } from 'ramda';

import {
  addTrackToCart,
  removeTracksFromCart,
  addReleaseToCart,
  removeReleaseFromCart,
  loadCartSummary,
} from 'actions/shoppingCart';
import analytics from 'helpers/analytics';
import c from './BuyButton.less';

const VISIBLE_FOR = 1500;
const ANIMATION_DURATION = 200;

class BuyButtonComponent extends React.PureComponent {
  static propTypes = {
    onAdd: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
    loggedIn: PropTypes.bool,
    isInCart: PropTypes.bool,
    children: PropTypes.element,
    size: PropTypes.string,
    alt: PropTypes.string,
  };

  static defaultProps = {
    loggedIn: false,
    isInCart: false,
    children: null,
    size: 'small',
    alt: null,
  };

  state = {
    showFeedBack: false,
  };

  componentWillUnmount() {
    if (this.tId) {
      clearTimeout(this.tId);
    }
  }

  handleAnimation = () => {
    if (this.state.showFeedBack) {
      clearTimeout(this.tId);

      this.setState({
        showFeedBack: false,
      });

      setTimeout(() => {
        this.setState({
          showFeedBack: true,
        });
      }, ANIMATION_DURATION);
    } else {
      this.setState({
        showFeedBack: true,
      });
    }

    this.tId = setTimeout(() => {
      this.setState({
        showFeedBack: false,
      });
    }, VISIBLE_FOR);
  };

  handleAddToCart = () => {
    return this.props.onAdd().then(this.handleAnimation);
  };

  handleRemoveFromCart = () => {
    return this.props.onRemove().then(this.handleAnimation);
  };

  render() {
    const {
      loggedIn, isInCart, size, history,
    } = this.props;

    return (
      <div className={c.btnWrapper}>
        {
          loggedIn ? (
            <Fragment>
              <span
                className={classNames(c.feedback, c[`feedback-${size}`], {
                  [c.showFeedback]: this.state.showFeedBack,
                  [c.removed]: !isInCart,
                })}
                data-text={isInCart ? 'Added to cart' : 'Removed from cart'}
              />
              <button
                type="button"
                className={classNames(c.btn, c[`btn-${size}`], { [c.inCart]: isInCart && loggedIn })}
                onClick={isInCart ? this.handleRemoveFromCart : this.handleAddToCart}
                aria-label={this.props.alt}
              >
                <span className={c.price}>
                  {this.props.children}
                </span>
                <span className={c.sep} />
                <span className={c.cart}>
                  {
                    isInCart && loggedIn ? (
                      <RemoveCartIcon className={c.cartIcon} />
                    ) : (
                      <AddCartIcon className={c.cartIcon} />
                    )
                  }
                </span>
              </button>
            </Fragment>
          ) : (
            <Link
              rel="nofollow"
              className={classNames(c.btn, c[`btn-${size}`])}
              to={`/shop/login?${stringify({ to: history.location.pathname + history.location.search })}`}
              aria-label="Login before adding to cart"
            >
              <span className={c.price}>
                {this.props.children}
              </span>
              <span className={c.sep} />
              <span className={c.cart}>
                <AddCartIcon className={c.cartIcon} />
              </span>
            </Link>
          )
        }
      </div>
    );
  }
}

const mapStateToProps = (store) => ({
  loggedIn: store.user.loggedIn,
});

const BuyButton = withRouter(connect(mapStateToProps)(BuyButtonComponent));

function BuyTrackButtonComponent(props) {
  const location = useLocation();
  const handleAddToCart = () => {
    analytics.trackEvent({
      category: pathOr(undefined, ['state', 'analytics', 'category'], location),
      action: 'Add',
      label: 'Track',
    });

    return props.addTrackToCart(props.track.id)
      .then(() => {
        props.loadCartSummary();
      })
      .catch(() => null);
  };

  const handleRemoveFromCart = () => {
    analytics.trackEvent({
      category: pathOr(undefined, ['state', 'analytics', 'category'], location),
      action: 'Remove',
      label: 'Track',
    });

    return props.removeTracksFromCart(props.track.id)
      .then(() => {
        props.loadCartSummary();
      })
      .catch(() => null);
  };

  return (
    <BuyButton
      onAdd={handleAddToCart}
      onRemove={handleRemoveFromCart}
      isInCart={props.track.inShoppingCart}
      alt={props.alt}
    >
      <span>
        $&nbsp;<strong>{numeral(props.track.price).format('0.00')}</strong>
      </span>
    </BuyButton>
  );
}

BuyTrackButtonComponent.propTypes = {
  addTrackToCart: PropTypes.func.isRequired,
  removeTracksFromCart: PropTypes.func.isRequired,
  loadCartSummary: PropTypes.func.isRequired,
  track: PropTypes.object.isRequired,
  alt: PropTypes.string,
};

BuyTrackButtonComponent.defaultProps = {
  alt: null,
};

export const BuyTrackButton = connect(
  null,
  {
    addTrackToCart,
    removeTracksFromCart,
    loadCartSummary,
  },
)(BuyTrackButtonComponent);

/**
 * @return {null}
 */
function BuyReleaseButtonComponent(props) {
  const { release, trackList } = props;
  const location = useLocation();

  const handleAddToCart = async () => {
    analytics.trackEvent({
      category: pathOr(undefined, ['state', 'analytics', 'category'], location),
      action: 'Add',
      label: 'Release',
    });

    return props.addReleaseToCart(release.id)
      .then(() => {
        props.loadCartSummary();
      })
      .catch(() => null);
  };

  const handleRemoveFromCart = async () => {
    analytics.trackEvent({
      category: pathOr(undefined, ['state', 'analytics', 'category'], location),
      action: 'Remove',
      label: 'Release',
    });

    return props.removeReleaseFromCart(release.id)
      .then(() => {
        props.loadCartSummary();
      })
      .catch(() => null);
  };

  const isInCart = release.tracks && release.tracks.length > 0
    ? release.tracks
      .map((id) => trackList[id])
      .filter((track) => !track.downloadUrl)
      .every((t) => t.inShoppingCart)
    : false;

  const releasePrice = (release.tracks || [])
    .map((id) => trackList[id])
    .filter((track) => !track.downloadUrl)
    .reduce((acc, track) => acc + track.price, 0);

  const areAllTracksBought = (release.tracks || [])
    .map((id) => trackList[id])
    .every((track) => track.downloadUrl);

  return !areAllTracksBought ? (
    <BuyButton onAdd={handleAddToCart} onRemove={handleRemoveFromCart} isInCart={isInCart} size="large" alt={props.alt}>
      <span>
        $&nbsp;<strong>{numeral(releasePrice).format('0.00')}</strong>
      </span>
    </BuyButton>
  ) : null;
}

BuyReleaseButtonComponent.propTypes = {
  addReleaseToCart: PropTypes.func.isRequired,
  removeReleaseFromCart: PropTypes.func.isRequired,
  loadCartSummary: PropTypes.func.isRequired,
  release: PropTypes.object.isRequired,
  trackList: PropTypes.object.isRequired,
  alt: PropTypes.string,
};

BuyReleaseButtonComponent.defaultProps = {
  alt: null,
};

export const BuyReleaseButton = connect(
  (store) => ({
    trackList: store.tracks.list,
  }),
  {
    addReleaseToCart,
    removeReleaseFromCart,
    loadCartSummary,
  },
)(BuyReleaseButtonComponent);

export default {
  BuyReleaseButton,
  BuyTrackButton,
};
